import { useEffect, useState } from 'react';
import { Table, Button, Space, Tag, Select, Input, message, DatePicker, Row, Col, Card } from 'antd';
import { SearchOutlined, FilterOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchTickets, cancelTicket } from '../store/slices/ticketsSlice';
import { fetchParkings } from '../store/slices/parkingsSlice';
import dayjs from 'dayjs';

const { Option } = Select;

export function TicketsPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { tickets, isLoading, total, currentPage, pageSize } = useSelector((state: RootState) => state.tickets);
  const { parkings } = useSelector((state: RootState) => state.parkings);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<any[]>([]);
  const [parkingFilter, setParkingFilter] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Charger les parkings au montage du composant
  useEffect(() => {
    dispatch(fetchParkings({}));
  }, [dispatch]);

  // Combined effect with debounce for search/filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const filters: any = {
        page: currentPage, 
        pageSize, 
        status: statusFilter,
        search: searchTerm.trim() // Nettoyer les espaces
      };

      // Ajouter les filtres de date si sélectionnés
      if (dateRange && dateRange[0] && dateRange[1]) {
        // Date de début : 00:00:00
        const startDate = dateRange[0].startOf('day').toISOString();
        // Date de fin : 23:59:59
        const endDate = dateRange[1].endOf('day').toISOString();
        
        filters.startDate = startDate;
        filters.endDate = endDate;
      }

      // Ajouter les filtres avancés
      if (parkingFilter) filters.parkingId = parkingFilter;

      // Log pour debug
      console.log('Dispatching fetchTickets avec filtres:', filters);

      dispatch(fetchTickets(filters));
    }, searchTerm || statusFilter || dateRange || parkingFilter ? 500 : 0); // Immediate for pagination, debounced for search

    return () => clearTimeout(timeoutId);
  }, [dispatch, currentPage, pageSize, statusFilter, searchTerm, dateRange, parkingFilter]);

  const handleCancelTicket = async (id: string) => {
    try {
      const result = await dispatch(cancelTicket(id)).unwrap();
      message.success(result.message || 'Ticket annulé avec succès');
    } catch (error: any) {
      message.error(error.message || 'Erreur lors de l\'annulation du ticket');
    }
  };

  const handleExportTickets = () => {
    // Fonction d'export des tickets
    const csvContent = [
      ['ID', 'Plaque', 'Utilisateur', 'Parking', 'Début', 'Fin', 'Prix (TND)', 'Statut'].join(','),
      ...tickets.map(ticket => [
        ticket.id.slice(-8),
        ticket.plate || 'N/A',
        ticket.user?.phone || 'N/A',
        ticket.parking?.name || 'N/A',
        ticket.startTime ? new Date(ticket.startTime).toLocaleString('fr-TN') : 'N/A',
        ticket.endTime ? new Date(ticket.endTime).toLocaleString('fr-TN') : 'N/A',
        ticket.amount ? (ticket.amount / 1000).toFixed(3) : 'N/A',
        getStatusText(ticket.status)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tickets_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Export réussi');
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSearchTerm('');
    setDateRange(null);
    setParkingFilter('');
  };

  const handleBulkCancel = async () => {
    if (selectedTickets.length === 0) {
      message.warning('Aucun ticket sélectionné');
      return;
    }

    try {
      const activeTickets = selectedTickets.filter(ticket => ticket.status === 'ACTIVE');
      if (activeTickets.length === 0) {
        message.warning('Aucun ticket actif sélectionné');
        return;
      }

      // Annuler tous les tickets actifs sélectionnés
      await Promise.all(
        activeTickets.map(ticket => 
          dispatch(cancelTicket(ticket.id)).unwrap()
        )
      );

      message.success(`${activeTickets.length} ticket(s) annulé(s)`);
      setSelectedRowKeys([]);
      setSelectedTickets([]);
    } catch (error) {
      message.error('Erreur lors de l\'annulation des tickets');
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: any[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedTickets(newSelectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => ({
      disabled: record.status !== 'ACTIVE', // Seuls les tickets actifs peuvent être sélectionnés
    }),
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'EXPIRED':
        return 'red';
      case 'CANCELED':
        return 'gray';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Actif';
      case 'EXPIRED':
        return 'Expiré';
      case 'CANCELED':
        return 'Annulé';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: 'Plaque',
      dataIndex: 'plate',
      key: 'plate',
      render: (plate: string) => plate || 'N/A',
    },
    {
      title: 'Utilisateur',
      dataIndex: ['user', 'phone'],
      key: 'user',
      render: (_: string, record: any) => record.user?.phone || 'N/A',
    },
    {
      title: 'Parking',
      dataIndex: ['parking', 'name'],
      key: 'parking',
      render: (_: string, record: any) => record.parking?.name || 'N/A',
    },
    {
      title: 'Début',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (date: string) => date ? new Date(date).toLocaleString('fr-TN') : 'N/A',
    },
    {
      title: 'Fin',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (date: string) => date ? new Date(date).toLocaleString('fr-TN') : 'N/A',
    },
    {
      title: 'Prix',
      dataIndex: 'amount',
      key: 'amount',
      render: (millimes: number) => millimes ? `${(millimes / 1000).toFixed(3)} TND` : 'N/A',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          {record.status === 'ACTIVE' && (
            <Button
              type="link"
              danger
              onClick={() => handleCancelTicket(record.id)}
            >
              Annuler
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">Tickets</h1>
        <Space direction="vertical" size="small" className="w-full sm:w-auto">
          {selectedTickets.length > 0 && (
            <Button 
              danger
              onClick={handleBulkCancel}
              disabled={selectedTickets.filter(t => t.status === 'ACTIVE').length === 0}
              className="w-full sm:w-auto"
              block={isMobile}
            >
              Annuler sélection ({selectedTickets.filter(t => t.status === 'ACTIVE').length})
            </Button>
          )}
          <Button 
            icon={<DownloadOutlined />} 
            onClick={handleExportTickets}
            disabled={tickets.length === 0}
            className="w-full sm:w-auto"
            block={isMobile}
          >
            Exporter
          </Button>
        </Space>
      </div>

      <Card>
        <div className="space-y-4">
          {searchTerm && (
            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
              <strong>Recherche active :</strong> "{searchTerm}" - {tickets.length} résultat(s) trouvé(s)
            </div>
          )}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Rechercher par plaque, utilisateur ou parking"
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Select
                placeholder="Filtrer par statut"
                value={statusFilter}
                onChange={setStatusFilter}
                allowClear
                className="w-full"
              >
                <Option value="ACTIVE">Actif</Option>
                <Option value="EXPIRED">Expiré</Option>
                <Option value="CANCELED">Annulé</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <DatePicker.RangePicker
                placeholder={['Date début', 'Date fin']}
                value={dateRange}
                onChange={setDateRange}
                className="w-full"
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Space direction="vertical" size="small" className="w-full sm:w-auto">
                <Button 
                  icon={<FilterOutlined />}
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="w-full sm:w-auto"
                  block={isMobile}
                >
                  Filtres avancés
                </Button>
                <Button 
                  onClick={clearFilters}
                  className="w-full sm:w-auto"
                  block={isMobile}
                >
                  Effacer
                </Button>
              </Space>
            </Col>
          </Row>

          {showAdvancedFilters && (
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Filtrer par parking"
                  value={parkingFilter}
                  onChange={setParkingFilter}
                  allowClear
                  className="w-full"
                  loading={parkings.length === 0}
                >
                  {parkings.map((parking) => (
                    <Option key={parking.id} value={parking.id}>
                      {parking.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          )}
        </div>
      </Card>

      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={tickets}
          loading={isLoading}
          rowKey="id"
          rowSelection={rowSelection}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: (page, size) => {
              dispatch({ type: 'tickets/setCurrentPage', payload: page });
              dispatch({ type: 'tickets/setPageSize', payload: size });
            },
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total}`,
          }}
        />
      </div>
    </div>
  );
}
